import { getApiErrorMessage } from '../../utils/apiError';

/**
 * Operator-facing messages for supplier-order commands. Backend prose can
 * mention implementation details (FIFO or margin), so stable codes must be
 * translated here before they reach an OPERATIONS user.
 */
const SUPPLIER_ORDER_ERROR_CODE_KEYS: Record<string, string> = {
  WAREHOUSE_RECONCILIATION_REQUIRED: 'orders:supplierOrders.detail.warehouseReconciliationRequired',
  SUPPLIER_ORDER_ALLOCATED_COST_EXCEEDED: 'orders:supplierOrders.detail.allocatedCostExceeded',
  ORDER_COMMERCIALLY_FROZEN: 'orders:supplierOrders.detail.commerciallyFrozen',
  SUPPLIER_ORDER_INVALID_STATUS_TRANSITION: 'orders:supplierOrders.detail.invalidStatusTransition',
  SUPPLIER_ORDER_HAS_DOCUMENTS: 'orders:supplierOrders.detail.hasDocuments',
  SUPPLIER_ORDER_CUSTOMER_ALLOCATION_BLOCKED: 'orders:supplierOrders.detail.customerAllocationBlocked',
  SUPPLIER_ORDER_PZ_REDUCTION_BLOCKED: 'orders:supplierOrders.detail.pzReductionBlocked',
  SUPPLIER_ORDER_CANCELLED: 'orders:supplierOrders.detail.supplierOrderCancelled',
  // SUPPLIER_CUSTOMER_LINK_RETIRED, WAREHOUSE_RECONCILIATION_REQUIRED and
  // SUPPLIER_ORDER_VALUATION_SETTLED_ORDER_BLOCKED are resolved below: their
  // wording depends on the order's cost basis / count.
};

export interface SupplierOrderErrorContext {
  /** Customer order cost basis when the command ran from the order page. */
  costBasis?: 'LEGACY' | 'WAREHOUSE_FIFO' | null;
}

/**
 * Codes describing a state the operator must change before the command can
 * ever succeed (a ZD closed by an invoice, a line already received on a PZ).
 * Their toasts stay on screen until dismissed, like the PZ/WZ blocking codes.
 */
const SUPPLIER_ORDER_BLOCKING_CODES = new Set([
  'ORDER_COMMERCIALLY_FROZEN',
  'SUPPLIER_CUSTOMER_LINK_RETIRED',
  'WAREHOUSE_RECONCILIATION_REQUIRED',
  'SUPPLIER_ORDER_VALUATION_SETTLED_ORDER_BLOCKED',
  'SUPPLIER_ORDER_INVALID_STATUS_TRANSITION',
  'SUPPLIER_ORDER_HAS_DOCUMENTS',
  'SUPPLIER_ORDER_CUSTOMER_ALLOCATION_BLOCKED',
  'SUPPLIER_ORDER_PZ_REDUCTION_BLOCKED',
  'SUPPLIER_ORDER_CANCELLED',
]);

export function isBlockingSupplierOrderError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && SUPPLIER_ORDER_BLOCKING_CODES.has(code);
}

export function getSupplierOrderErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>,
  context: SupplierOrderErrorContext = {},
): string {
  const data = error?.response?.data;
  const code = data?.code;
  if (code === 'SUPPLIER_CUSTOMER_LINK_RETIRED') {
    // A LEGACY order still stands on its historical ZD↔order links; say so
    // instead of only "we receive purchases to stock" (owner decision 2026-09-18).
    return t(context.costBasis === 'LEGACY'
      ? 'orders:supplierOrders.detail.customerLinkRetiredLegacy'
      : 'orders:supplierOrders.detail.customerLinkRetired');
  }
  if (code === 'SUPPLIER_ORDER_VALUATION_SETTLED_ORDER_BLOCKED') {
    // Owner decision 2026-09-21: an ordinary LEGACY link is now a confirmable
    // warning; only a settled or invoice-frozen customer order stays blocked.
    const numbers: string[] = Array.isArray(data?.details?.settledOrderNumbers) ? data.details.settledOrderNumbers : [];
    return t('orders:supplierOrders.detail.valuationSettledOrderBlocked', {
      count: Number(data?.details?.settledOrderCount) || numbers.length,
      numbers: numbers.join(', ') || '—',
    });
  }
  if (code === 'WAREHOUSE_RECONCILIATION_REQUIRED') {
    const count = Number(data?.details?.unreconciledOrderCount);
    const numbers: string[] = Array.isArray(data?.details?.unreconciledOrderNumbers) ? data.details.unreconciledOrderNumbers : [];
    return Number.isFinite(count) && count > 0
      ? t('orders:supplierOrders.detail.warehouseReconciliationRequiredCount', { count, numbers: numbers.join(', ') || '—' })
      : t('orders:supplierOrders.detail.warehouseReconciliationRequired');
  }
  if (typeof code === 'string' && SUPPLIER_ORDER_ERROR_CODE_KEYS[code]) {
    return t(SUPPLIER_ORDER_ERROR_CODE_KEYS[code], fallbackParams);
  }
  return getApiErrorMessage(error, t, fallbackKey, undefined, fallbackParams);
}
